<script setup lang="ts">
import type { PortableTextBlock } from '@sanity/types';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import { baseTitleVariants } from './variants';

interface Props {
  variant?: NonNullable<Parameters<typeof baseTitleVariants>[0]>['variant'];
  size?: NonNullable<Parameters<typeof baseTitleVariants>[0]>['size'];
  preTitle?: string | Array<PortableTextBlock>;
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  description?: string | Array<PortableTextBlock>;
  title?: string | Array<PortableTextBlock>;
}

const props = defineProps<Props>();

const headerComponent = computed(() => {
  if (props.tag) {
    return props.tag;
  }

  switch (props.size) {
    case 'tiny':
      return 'h4';
    case 'small':
      return 'h3';
    case 'large':
      return 'h1';
    default:
      return 'h2';
  }
});

</script>

<template>
  <div :class="baseTitleVariants({ variant: props.variant, size: props.size })">
    <PortableEditor v-if="props.preTitle" :content="props.preTitle" class="c-base-title__pre-title" />
    <component :is="headerComponent" v-if="props.title" class="c-base-title__header">
      <PortableEditor :content="props.title" heading-content />
    </component>
    <slot name="description">
      <PortableEditor v-if="props.description" :content="props.description" class="c-base-title__description" />
    </slot>
  </div>
</template>

<style scoped>
@import '../../../../ui/assets/css/mixins/_mixins.index.pcss';

.c-base-title__pre-title {
  @mixin h4;
  color: var(--var-title-pre-title-color);
  display: block;
  text-transform: uppercase;
  line-height: 1.5;
  padding-bottom: 0.5rem;
}

.c-base-title__header {
  @mixin h2;
  color: var(--var-title-header-color);
  display: block;
  text-transform: uppercase;
}

.c-base-title__description {
  @mixin body-large-size;
  margin-top: theme('margin.8');

  @screen tablet-wide {
    margin-top: theme('margin.12');
  }
}

.c-base-title--left {
  text-align: center;

  @screen tablet-wide {
    text-align: left;
  }
}

.c-base-title--center {
  text-align: center;
}

.c-base-title--right {
  text-align: end;
}

.c-base-title--small {
  & .c-base-title__header {
    @mixin h3;
  }
}

.c-base-title--large {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  & .c-base-title__header {
    @mixin hero;
  }
}

.c-base-title--tiny {
  & .c-base-title__header {
    @mixin h6;

    @screen tablet-wide {
      @mixin h4;
    }
  }
}

.c-base-title--bag {
  & .c-base-title__header {
    @mixin h2-highlight;
    text-transform: none;
  }
  & .c-base-title__pre-title {
    @mixin h2;
    color: var(--var-title-header-color);
  }
}

</style>
